import { ClassNames, css } from '@emotion/react';
import { AnimatePresence, motion } from 'framer-motion';
import React, { Key } from 'react';
import { forwardRef } from 'react';

import { fontStyles } from '~src/theme/fontStyles';

import { colors } from '../theme/colors';
import { ThemeModel } from '../theme/theme';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  size?: 'small' | 'normal';
  buttonType?: 'primary' | 'secondary' | 'tertiary';
  noWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ size, children, buttonType, noWidth, className, ...htmlButtonProps }, ref) => {
    const getButtonStyle = (type: ButtonProps['buttonType']) => {
      if (type === 'secondary') {
        return secondaryStyle;
      } else if (type === 'tertiary') {
        return tertiaryStyle;
      } else {
        return primaryStyle;
      }
    };

    return (
      <ClassNames>
        {({ cx }) => (
          <button
            {...htmlButtonProps}
            ref={ref}
            className={cx(className, 'focus-visible-button')}
            css={[styles.baseButton(size, noWidth), getButtonStyle(buttonType)]}
          >
            {children}
          </button>
        )}
      </ClassNames>
    );
  }
);

interface AnimatedProps extends ButtonProps {
  animKey: Key;
}

export const AnimatedButton = forwardRef<HTMLButtonElement, AnimatedProps>(
  ({ animKey, children, ...props }, ref) => (
    <Button
      {...props}
      ref={ref}
      css={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <AnimatePresence initial={false} exitBeforeEnter>
        <motion.div
          css={{
            overflow: 'hidden',
          }}
          initial={{ y: 25, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -25, opacity: 0 }}
          transition={{ type: 'just', duration: 0.1 }}
          key={animKey}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </Button>
  )
);

const primaryStyle = (theme: ThemeModel) =>
  css({
    borderColor: 'transparent',
    background: theme.accent48,
    color: colors.white,
  });

const secondaryStyle = (theme: ThemeModel) =>
  css({
    borderColor: theme.accent48,
    background: colors.white,
    color: colors.black,
  });

const tertiaryStyle = (theme: ThemeModel) =>
  css({
    borderColor: 'transparent',
    background: colors.white,
    color: theme.accent48,
  });

const styles = {
  baseButton: (size: ButtonProps['size'], noWidth: ButtonProps['noWidth']) => () =>
    css({
      height: size === 'small' ? '50px' : '60px',
      cursor: 'pointer',
      width: noWidth ? undefined : '100%',
      borderRadius: '2px',
      borderWidth: '1px',
      borderStyle: 'solid',
      padding: `${size === 'small' ? '10px' : '19px'} 20px`,
      ...fontStyles.px14_20,
      fontVariationSettings: '"wght" 500',
      ':hover': {
        opacity: 0.7,
      },
      ':disabled': {
        opacity: 0.5,
      },
      ':focus': {
        opacity: 0.7,
      },
    }),
};
